import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  History,
  Pencil,
  RotateCcw,
  Send,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { api, ApiError } from "@/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { formatDateTime } from "@/lib/format";
import { useAuth } from "@/auth";
import {
  CURRENCY,
  JOURNAL_STATUS_BADGE,
  todayIso,
  type GlJournalDetail,
} from "@/lib/gl";

function fmt(n: number) {
  return `${CURRENCY} ${Number(n || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

export default function GeneralLedgerJournalDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  const canPost = can("gl.post");
  const canWrite = can("gl.write");

  const [data, setData] = useState<GlJournalDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseDate, setReverseDate] = useState(todayIso());
  const [reverseDesc, setReverseDesc] = useState("");

  const load = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const d = await api.get<{ journal: GlJournalDetail }>(
        `/gl/journals/${id}`,
      );
      setData(d.journal);
    } catch (err) {
      toast.error((err as Error).message || "Failed to load journal");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const post = async () => {
    if (!id) return;
    setBusy(true);
    try {
      await api.post(`/gl/journals/${id}/post`);
      toast.success("Journal posted");
      await load();
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? String((e.body as { error?: string } | null)?.error ?? e.message)
          : (e as Error).message;
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const reverse = async () => {
    if (!id) return;
    setBusy(true);
    try {
      const res = await api.post<{ id: number; reference: string }>(
        `/gl/journals/${id}/reverse`,
        {
          entry_date: reverseDate,
          description: reverseDesc.trim() || undefined,
        },
      );
      toast.success(`Reversed via ${res.reference}`);
      setReverseOpen(false);
      navigate(`/general-ledger/journals/${res.id}`);
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? String((e.body as { error?: string } | null)?.error ?? e.message)
          : (e as Error).message;
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const voidDraft = async () => {
    if (!id) return;
    if (!confirm("Void this draft journal? This cannot be undone.")) return;
    setBusy(true);
    try {
      await api.del(`/gl/journals/${id}`);
      toast.success("Draft voided");
      await load();
    } catch (e) {
      toast.error((e as Error).message || "Void failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Card className="shadow-card">
        <CardContent className="py-10 text-center text-muted-foreground">
          Loading…
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const isDraft = data.status === "draft";
  const isPosted = data.status === "posted";
  const isReversed = data.status === "reversed";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button asChild variant="ghost" size="sm">
          <Link to="/general-ledger/journals">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to journals
          </Link>
        </Button>
        <div className="flex items-center gap-2 flex-wrap">
          {isDraft && canWrite && (
            <>
              <Button asChild variant="outline">
                <Link to={`/general-ledger/journals/new?edit=${id}`} onClick={(e) => {
                  e.preventDefault();
                  navigate(`/general-ledger/journals/${id}/edit`);
                }}>
                  <Pencil className="h-4 w-4 mr-1" />
                  Edit
                </Link>
              </Button>
              <Button variant="outline" onClick={voidDraft} disabled={busy}>
                <Trash2 className="h-4 w-4 mr-1" />
                Void
              </Button>
            </>
          )}
          {isDraft && canPost && (
            <Button onClick={post} disabled={busy || !data.balanced}>
              <Send className="h-4 w-4 mr-1" />
              Post
            </Button>
          )}
          {isPosted && canPost && (
            <Button
              variant="outline"
              onClick={() => {
                setReverseDate(todayIso());
                setReverseDesc(`Reversal of ${data.reference}`);
                setReverseOpen(true);
              }}
              disabled={busy}
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Reverse
            </Button>
          )}
        </div>
      </div>

      <Card className="shadow-card">
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="text-xl font-mono">{data.reference}</CardTitle>
            <CardDescription>{data.description || "—"}</CardDescription>
          </div>
          <Badge
            variant="outline"
            className={`capitalize text-sm ${JOURNAL_STATUS_BADGE[data.status] ?? ""}`}
          >
            {data.status}
          </Badge>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-3 text-sm">
          <div>
            <div className="text-muted-foreground">Entry Date</div>
            <div className="font-medium">{data.entry_date}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Source</div>
            <div className="font-medium capitalize">
              {data.source.replace(/_/g, " ")}
              {data.source_id ? ` #${data.source_id}` : ""}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">Posted</div>
            <div className="font-medium">
              {data.posted_at ? formatDateTime(data.posted_at) : "—"}
            </div>
          </div>
          {isReversed && (
            <div className="sm:col-span-3">
              <div className="text-muted-foreground">Reversed by</div>
              <div className="font-medium">
                {data.reversed_by_journal_id ? (
                  <Link
                    to={`/general-ledger/journals/${data.reversed_by_journal_id}`}
                    className="text-primary hover:underline font-mono"
                  >
                    Journal #{data.reversed_by_journal_id}
                  </Link>
                ) : (
                  "—"
                )}
              </div>
            </div>
          )}
          {data.reverses_journal_id && (
            <div className="sm:col-span-3">
              <div className="text-muted-foreground">Reverses</div>
              <Link
                to={`/general-ledger/journals/${data.reverses_journal_id}`}
                className="text-primary hover:underline font-mono"
              >
                Journal #{data.reverses_journal_id}
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="text-lg">Lines</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[60px]">#</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Memo</TableHead>
                <TableHead className="text-right">Debit</TableHead>
                <TableHead className="text-right">Credit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.lines.map((l) => (
                <TableRow key={l.id}>
                  <TableCell className="font-mono text-muted-foreground">
                    {l.line_no}
                  </TableCell>
                  <TableCell>
                    <span className="font-mono mr-2">{l.account_code}</span>
                    {l.account_name}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {l.description || "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {l.debit > 0 ? fmt(l.debit) : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {l.credit > 0 ? fmt(l.credit) : "—"}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="bg-muted/40 font-semibold">
                <TableCell colSpan={3} className="text-right">
                  Totals
                </TableCell>
                <TableCell className="text-right font-mono">
                  {fmt(data.total_debit)}
                </TableCell>
                <TableCell className="text-right font-mono">
                  {fmt(data.total_credit)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="shadow-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="h-5 w-5 text-muted-foreground" />
            Audit Trail
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Payload</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.audit.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-6 text-muted-foreground">
                    No audit entries.
                  </TableCell>
                </TableRow>
              ) : (
                data.audit.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>{formatDateTime(a.created_at)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {a.action.replace(/_/g, " ")}
                      </Badge>
                    </TableCell>
                    <TableCell>{a.actor_id ?? "System"}</TableCell>
                    <TableCell className="font-mono text-xs max-w-[60ch] truncate">
                      {a.payload || ""}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={reverseOpen} onOpenChange={setReverseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reverse Journal</DialogTitle>
            <DialogDescription>
              Creates a new posted journal with swapped debits and credits and
              links both entries.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="rdate">Reversal Date</Label>
              <Input
                id="rdate"
                type="date"
                value={reverseDate}
                onChange={(e) => setReverseDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="rdesc">Description</Label>
              <Textarea
                id="rdesc"
                rows={2}
                value={reverseDesc}
                onChange={(e) => setReverseDesc(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReverseOpen(false)}>
              Cancel
            </Button>
            <Button onClick={reverse} disabled={busy}>
              Reverse
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
